import { Component, OnInit, Input, ChangeDetectorRef, ChangeDetectionStrategy, ViewChild } from '@angular/core';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';

import { Subject } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';

import { Store } from '@ngrx/store';

import { AppState, Selectors, Actions } from 'store/root-reducer';
import { TableNameType, TablePreferenceType, TablesPreference, TableSortPreference } from 'modules/shared/models/table-preference.models';
import { ITasksRes, Task } from 'modules/shared/models/task.models';
import { TasksService } from 'modules/dashboard/services/tasks.service';
import { SnackbarService } from 'app/services/snackbar.service';

@Component({
  selector: 'artemis-task-table',
  templateUrl: './task-table.component.html',
  styleUrls: ['./task-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskTableComponent implements OnInit {

  @ViewChild(MatSort, {static: true}) set sort(sort: MatSort) {
    this.tasksDataSource.sort = sort;
  }

  @ViewChild(MatPaginator, {static: true}) set paginator(paginator: MatPaginator) {
    this.tasksDataSource.paginator = paginator;
  }

  @Input() set taskType(type: 'pending' | 'overdue') {
    if (type) {
      this.currentTasksType = type;

      if (type === 'overdue') {
        this.showErrorIcon = true;
      }

      this.tableNameType = type.toUpperCase() + '_TASK_TABLE';
      this.getTaskTablePreference();
    }
  }

  private unsubscribe$: Subject<void> = new Subject();

  readonly displayedColumns: Array<string> = ['name', 'type', 'date'];
  readonly pageSizeOptions: Array<number> = [5, 10, 15];

  isLoading: boolean = false;
  isActive: string = 'type';
  currentTasksType: string;

  tasksDataSource: MatTableDataSource<Task> = new MatTableDataSource([]);
  preferredTaskTablePageSize: number = this.pageSizeOptions[0];
  preferredTaskTableDataSourceLength: number;

  currentPage: number = 0;
  currentTableSort: string;

  tableNameType: string = '';
  showErrorIcon: boolean = false;

  constructor(private cdRef: ChangeDetectorRef,
              private taskService: TasksService,
              private snackBar: SnackbarService,
              private store: Store<AppState>) { }

  ngOnInit() {
  }

  /**
   * Get table pagination preference from store
   * @method getTaskTablePreference
   */
  private getTaskTablePreference(): void {
    this.store.select(Selectors.userPreference.getTablesPreference)
      .pipe(
        takeUntil(this.unsubscribe$),
        take(1)
      )
      .subscribe((tablesPreference: TablesPreference) => {
        const tablePreference = tablesPreference[TableNameType[this.tableNameType]];
        if (tablePreference) {
          if (tablePreference[TablePreferenceType.PAGE_SIZE]) {
            this.preferredTaskTablePageSize = tablePreference[TablePreferenceType.PAGE_SIZE];
          }

          if (tablePreference[TablePreferenceType.SORT]) {
            this.tasksDataSource.sort.active = tablePreference[TablePreferenceType.SORT].active;
            this.tasksDataSource.sort.direction = tablePreference[TablePreferenceType.SORT].direction;
          }

          this.cdRef.detectChanges();
        }
        this.getTasks();
      });
  }

/**
   * Get page with (pending, overdue case)
   * @method getTasks
   */
 private getTasks(): void {
    this.isLoading = true;
    this.cdRef.detectChanges();

    this.taskService
      .getTasks(this.currentTasksType, {
        limit: this.preferredTaskTablePageSize,
        offset: this.preferredTaskTablePageSize * this.currentPage,
        sort: this.currentTableSort
      })
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe(
        (documentsRes: ITasksRes) => {
          this.preferredTaskTableDataSourceLength = documentsRes.count;
          if (this.tasksDataSource) {
            this.tasksDataSource.data = documentsRes.results;
          } else {
            this.tasksDataSource = new MatTableDataSource(documentsRes.results);
          }

          this.tasksDataSource.paginator = this.paginator;

          this.isLoading = false;
          this.cdRef.detectChanges();
        },
        (error: any) => {
          this.isLoading = false;
          this.snackBar.error('Error: Failed to load list of tasks');
          this.cdRef.detectChanges();
        }
      );
  }

  /**
   * Pagination event handler
   * @method tablePaginationChangeHandler
   */
  tablePaginationChangeHandler(event: PageEvent): void {
    if (event.pageSize !== this.preferredTaskTablePageSize) {
      this.store.dispatch(new Actions.userPreference.SetPaginationPreference(
        {tableName: TableNameType[this.tableNameType], pageSize: event.pageSize}
      ));

      this.preferredTaskTablePageSize = event.pageSize;
    }
    this.currentPage = event.pageIndex;
    this.getTasks();
  }

  /**
   * Add sort information to preference store
   * @method tableSortChangeHandler
   */
  tableSortChangeHandler(event: Sort): void {
    this.store.dispatch(new Actions.userPreference.SetSortPreference({
      tableName: TableNameType[this.tableNameType],
      [TablePreferenceType.SORT]: <TableSortPreference>{
        active: event.active,
        direction: event.direction
      }
    }));
    switch (event.direction) {
        case 'asc':
          this.currentTableSort = `${event.active}`;
          break;
        case 'desc':
          this.currentTableSort = `-${event.active}`;
          break;
        default:
          this.currentTableSort = undefined;
          break;
      }
      this.getTasks();
  }
}
